<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2019-2024 (original work) Open Assessment Technologies SA ;
    import { createEventDispatcher, onMount, onDestroy, tick } from 'svelte';
    import { __, tabFocus, Validity } from '@oat-sa-private/ui-core';
    import { applyInputTextareaMaxlength, textareaConstrainMaxWords } from '../input/customMaxlength.js';

    const resizableTypes = {
        none: 'none',
        both: 'both',
        horizontal: 'horizontal',
        vertical: 'vertical'
    };

    // native HTML props
    export let cols = 25;
    export let disabled = false;
    export let maxlength;
    export let minlength;
    export let name;
    export let placeholder;
    export let readonly = false;
    export let required = false;
    export let rows = 3;
    export let id;
    export let spellcheck;

    // custom props
    export let value = '';
    export let label = '';
    export let hiddenLabel = __('Textarea');
    export let maxlengthAttrEnabled = true;
    export let wordSeparators;
    export let maxWords;
    export let resizable = resizableTypes.none;
    export let pattern;
    export let customValidity = '';
    export let visited = false; // true after first focus
    export let fullwidth = false;

    if (!resizableTypes[resizable]) {
        resizable = resizableTypes.none;
    }

    const dispatch = createEventDispatcher();
    const validation = new Validity();

    let textareaElement;
    let focusFromKey = false; //class toggler instead of :focus-visible to prevent conflict with native
    let countsForValue = {};
    let previousRestrictedValue = value;
    let lastCursorPosition = Infinity; //for maxWords, shouldn't be updated during 'composition'

    const wordRegex = new RegExp(`[^${wordSeparators || '\\s.,:;?!&#%/*+='}]+`, 'g');

    function getCounts(val) {
        if (!countsForValue[val]) {
            const wordMatches = (typeof val === 'string' && val.match(wordRegex)) || [];
            countsForValue = {
                [val]: {
                    words: wordMatches.length,
                    chars: val.length
                }
            };
        }
        return countsForValue[val]; //'cache' to not run regex twice
    }

    $: validation.setPattern(pattern).setRequired(required).setLongLimitation(maxlength).setShortLimitation(minlength);

    $: dispatch('change', {
        value,
        count: getCounts(value)
    });

    $: if (pattern) {
        validation.run(value).then(validity => {
            dispatch('validation', validity);
            // textareaElement element can be removed before the response will be resolved
            if (visited && validity.patternMismatch) {
                customValidity = 'Pattern mismatch';
            } else {
                customValidity = '';
            }
        });
    } else {
        dispatch('validation', validation.get(value));
    }
    $: if (textareaElement && visited) {
        textareaElement.setCustomValidity(customValidity);
    }

    /**
     * @param {InputEvent|CompositionEvent} e
     */
    function onInput(e) {
        applyInputTextareaMaxlength(e, maxlengthAttrEnabled && maxlength);

        const newCounts = getCounts(e.target.value);
        textareaConstrainMaxWords(e, {
            maxWords,
            wordCount: newCounts.words,
            wordSeparators,
            previousValue: previousRestrictedValue,
            lastCursorPosition
        });

        value = e.target.value;

        const isComposing = e instanceof InputEvent && e.isComposing;
        if (!isComposing) {
            previousRestrictedValue = value;
        }
    }

    /**
     * For language keyboards with autocomplete
     *  @param {CompositionEvent} e
     */
    function onCompositionEnd(e) {
        onInput(e);
    }

    function onBlur() {
        visited = true;
        focusFromKey = false;
        dispatch('blur', { target: textareaElement });
    }

    function onFocus() {
        dispatch('focus', { target: textareaElement });
    }

    onMount(() => {
        tick().then(() => {
            dispatch('ready', {
                count: getCounts(value)
            });
        });
    });

    onDestroy(() => {
        validation.stop();
    });

    function triggerClipboardEvent(e) {
        const domEventType = e.type;
        const selectedContent = document.getSelection().toString();
        let content;
        let replacedContent;

        if (domEventType === 'paste') {
            content = e.clipboardData.getData('text/plain');
            replacedContent = selectedContent;
        } else {
            content = selectedContent;
        }

        dispatch('clipboard', {
            domEventType,
            target: e.target,
            content,
            replacedContent,
            position: e.target.selectionStart
        });
    }

    function triggerDragEvent(e) {
        dispatch('dragdrop', {
            domEventType: e.type,
            content: document.getSelection().toString(),
            position: e.target.selectionStart,
            target: e.target
        });
    }

    function triggerDropEvent(e) {
        const content = e.dataTransfer.getData('text/plain');
        dispatch('dragdrop', {
            domEventType: 'drop',
            content,
            target: e.target
        });
    }

    function handleSelectionChange() {
        //= if not composing with autocomplete
        if (value === previousRestrictedValue) {
            lastCursorPosition = textareaElement.selectionEnd;
        }
    }
</script>

<style>
    label {
        display: inline-flex;
        flex-direction: column;
        align-items: flex-start;
    }
    .label-text {
        margin-inline: 0 var(--space-1x);
        margin-block: 0 var(--space-1x);
        font-size: var(--fontsize-heading);
        @add-mixin bold;
    }
    [title='required'] {
        color: var(--color-alert);
        padding-inline: var(--space-half);
        padding-block: 0;
    }
    .positioning-wrapper {
        display: inline-block;
        position: relative;
    }

    textarea {
        color: inherit;
        background: inherit;
        letter-spacing: inherit;
        word-spacing: inherit;

        padding-inline: 2rem;
        padding-block: 1.5rem;
        border: var(--border-thin) solid var(--color-border-default);
        font-family: var(--font-ui);
        font-size: var(--fontsize-body);
        line-height: var(--line-height-default);

        &.resize-none {
            resize: none;
        }

        &.resize-both {
            resize: both;
        }

        &.resize-horizontal {
            resize: inline;
        }

        &.resize-vertical {
            resize: block;
        }
    }

    .fullwidth,
    .fullwidth .positioning-wrapper {
        display: block;
    }
    .fullwidth textarea {
        min-inline-size: 100%;
    }

    textarea:hover,
    textarea:focus,
    textarea:active {
        outline: none;
        @add-mixin double-border var(--color-border-actionable-hover);
        border: var(--border-thin) solid var(--color-border-actionable-hover);
    }

    textarea.key-focus-visible {
        @add-mixin double-border var(--color-border-selected);
        @add-mixin simple-outline;
    }

    textarea.visited:invalid,
    textarea.visited.key-focus-visible:invalid,
    textarea.invalid,
    textarea.invalid.key-focus-visible {
        @add-mixin double-border var(--color-border-warning);
    }

    textarea[readonly],
    textarea[readonly].key-focus-visible,
    textarea:disabled,
    textarea:disabled:hover,
    textarea.key-focus-visible:disabled {
        color: var(--color-text-disabled);
        border: var(--border-thin) solid var(--color-border-disabled);
        box-shadow: none;
    }
    textarea:disabled {
        background: var(--color-bg-disabled-subtle);
    }
</style>

<label class:fullwidth>
    <div class="label-text" class:visually-hidden={!label}>
        {label || hiddenLabel}
        {#if label && required}<abbr title="required" aria-hidden="true">*</abbr>{/if}
    </div>
    <div class="positioning-wrapper">
        <textarea
            use:tabFocus
            bind:this={textareaElement}
            class={`resize-${resizable}`}
            class:key-focus-visible={focusFromKey}
            class:visited
            class:invalid={customValidity}
            id={id ? id : void 0}
            {cols}
            {name}
            {placeholder}
            {required}
            {rows}
            maxlength={maxlengthAttrEnabled ? maxlength : void 0}
            {minlength}
            {readonly}
            {disabled}
            {value}
            {spellcheck}
            on:input={onInput}
            on:input
            on:compositionend={onCompositionEnd}
            on:keyup
            on:focus={onFocus}
            on:copy={triggerClipboardEvent}
            on:paste={triggerClipboardEvent}
            on:cut={triggerClipboardEvent}
            on:dragstart={triggerDragEvent}
            on:dragend={triggerDragEvent}
            on:drop={triggerDropEvent}
            on:selectionchange={handleSelectionChange}
            on:tabfocus={() => {
                focusFromKey = true;
            }}
            on:blur={onBlur} />
    </div>
</label>
